import createApiClient from '@/api/axiosInstance';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import ShortlistedManuCard from '@/js/components/card/ShortlistedManuCard';
import CompareModal from '@/js/components/modals/CompareModal';
import {
  addShortlistedAction,
  setSearchResultAction,
} from '@/js/reduxstore/actions/searchActions';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const ShortlistedManufacturerScreen = ({ setScreen }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const apiClient = createApiClient(navigate, dispatch);

  const { projectId, projectName } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reduxShortlistData = useSelector(
    (state) => state?.searchReducer?.shortlisted,
  );
  const shortlistedData = reduxShortlistData?.shortlisted;
  const [selected, setSelected] = useState([]);
  const [shortlisted, setShortlisted] = useState([]);

  // compare
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareError, setCompareError] = useState(null);

  const handleCompare = () => {
    if (selected.length === 0) {
      setCompareError('Select Manufacturers to compare');
      return;
    }

    setCompareError(null);
    setShowCompareModal(true);
  };

  // const toggleSelect = (item) => {
  //   const updatedSelectedToCompare = selectedToCompare.some(
  //     (manufacturer) => manufacturer.manufacturer_id === item.manufacturer_id,
  //   )
  //     ? selectedToCompare.filter(
  //         (manufacturer) =>
  //           manufacturer.manufacturer_id !== item.manufacturer_id,
  //       )
  //     : [...selectedToCompare, item];

  //   setSelectedToCompare(updatedSelectedToCompare);
  // };

  useEffect(() => {
    const getShortlistedManufacturers = async () => {
      setLoading(true);
      try {
        const response = await apiClient.post(
          '/api/shortlisted-manufacturer/get/manufacturer-card',
          JSON.stringify({
            project_id: projectId,
          }),
        );

        const responseData = response.data;
        if (responseData?.data) {
          console.log(responseData?.data);
          setShortlisted(responseData?.data);
        }
      } catch (error) {
        const error_message = error?.response?.data?.error_message;
        console.error('Error: ', error_message || error);
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      getShortlistedManufacturers();
    } else {
      setShortlisted(shortlistedData);
    }
  }, []);

  const toggleSelect = (item) => {
    setSelected((prev) =>
      prev.some((manu) => manu === item.manufacturer_id)
        ? prev.filter((manu) => manu !== item.manufacturer_id)
        : [...prev, item.manufacturer_id],
    );
  };

  const handleSendRFQ = () => {
    if (!projectId) {
      setScreen('PROJECT_DETAILS');
      return;
    }

    if (shortlisted.length === 0) {
      setError('Please add shortlisted Manufacturers first');
      return;
    }

    setError(null);
    navigate(`/project/createRFQ/${projectName}/${projectId}`, {
      state: {
        alreadySelected: selected,
      },
    });
  };

  const handleResumeSearch = () => {
    if (projectId) {
      dispatch(
        addShortlistedAction({
          description: '',
          shortlisted: shortlisted,
        }),
      );
      navigate(`/search/${projectName}/${projectId}`);
      return;
    }

    navigate('/search');
  };

  return (
    <div
      className="bg-white rounded-lg font-roboto max-h-[calc(100vh-60px)] overflow-y-auto"
      style={{
        scrollbarWidth: 'none',
        scrollbarColor: '#7636D9 #f0f0f0',
      }}
    >
      <div className="py-6 px-10 h-[calc(100vh-62px)]">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-lg font-medium">Shortlisted Manufactures</h1>
          <div className="flex items-center gap-3">
            {compareError && (
              <p className="bg-[#F8D7D7] text-[#B64444] text-sm py-2 px-3 rounded-lg">
                {compareError}
              </p>
            )}
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="text-darkPurple font-medium border border-darkPurple rounded px-2 hover:text-darkerPurple hover:bg-veryLightPurple hover:bg-opacity-15 ease-in duration-200 py-2"
                    onClick={handleCompare}
                  >
                    Compare
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="bg-black text-white text-xs rounded px-2 py-1 rounded"
                >
                  Select to Compare
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button
              variant="outline"
              type="submit"
              className={`w-[140px] rounded text-base text-darkPurple transition ease-in-out duration-300 bg-white hover:bg-veryVeryLightPurple hover:text-darkPurple border-darkPurple`}
              onClick={handleResumeSearch}
            >
              Resume Search
            </Button>
            <Button
              variant="outline"
              type="submit"
              className={`w-[100px] rounded text-base text-white transition ease-in-out duration-300 bg-darkPurple hover:bg-darkerPurple hover:text-white flex items-center justify-center`}
              onClick={handleSendRFQ}
            >
              Send RFQ
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="col-span-9 w-full min-h-[calc(100vh-70px)] bg-white rounded p-4 gap-4">
            <div className="flex flex-row items-center gap-2 text-darkPurple">
              <Loader2 className="h-8 w-8 animate-spin " />
              <span className="text-3xl">Loading</span>
            </div>
          </div>
        ) : (
          <div
            className="grid grid-cols-3 gap-16 overflow-y-auto relative pb-4"
            style={{
              scrollbarWidth: 'none',
              scrollbarColor: '#B2BBC6 #F2F4F7',
            }}
          >
            {shortlisted.length > 0 &&
              shortlisted.map((result, index) => (
                <ShortlistedManuCard
                  key={index}
                  item={result}
                  index={index}
                  selected={selected}
                  toggleSelect={toggleSelect}
                />
              ))}

            {shortlisted.length === 0 && (
              <div className="col-span-2">
                Currently You don't have any shortlisted Manufactures, Resume
                search to add any.
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="w-full flex items-center justify-center mt-4 ">
            <p className="bg-[#F8D7D7] text-[#B64444] text-sm py-3 px-3 rounded-lg">
              {error}
            </p>
          </div>
        )}

        <CompareModal
          screen={'SHORTLIST'}
          open={showCompareModal}
          selectedToCompare={shortlisted}
          toggleSelect={toggleSelect}
          onClose={() => {
            setShowCompareModal(false);
          }}
        />
      </div>
    </div>
  );
};

export default ShortlistedManufacturerScreen;
